import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authAPI } from '../services/api';
import type { SessionData } from '../types/api';
import { authKeys } from './keys';

/** What the API reports for a visitor with no session and connections disabled. */
const anonymousSession: SessionData = { connectEnabled: false, manager: null };

/**
 * Reads the Sorare connection state from `GET /auth/session`.
 *
 * `connectEnabled` and `manager` are **independent** fields and this hook deliberately keeps
 * them that way — it does not derive a single "connected" boolean. The API can report
 * `connectEnabled: false` (OAuth credentials unset — the rollback switch) while still
 * resolving `manager` for a session established earlier, so the consuming component decides
 * what each field gates: `manager` drives the connected UI, `connectEnabled` drives whether a
 * *new* connection can be started.
 *
 * `retry: false` because "not connected" is a normal `200` (not an error), so a rejection here
 * is a genuine failure worth surfacing once rather than hammering. The response is normalized
 * to the full shape — the envelope schema (`schemas/auth.ts`) validates leaves as `nullish`,
 * so a partial payload becomes the anonymous default instead of `undefined` fields.
 */
export const useSession = () =>
  useQuery({
    queryKey: authKeys.session(),
    queryFn: async ({ signal }): Promise<SessionData> => {
      const data = await authAPI.getSession({ signal });
      return {
        connectEnabled: data?.connectEnabled ?? anonymousSession.connectEnabled,
        manager: data?.manager ?? anonymousSession.manager,
      };
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

/**
 * Ends the Sorare session. Invalidates `authKeys.all` (a prefix of `authKeys.session()`) so the
 * session refetches and the UI drops back to the disconnected state. The invalidation lives on
 * this hook rather than the `mutate` call site because the Disconnect control sits in a dropdown
 * that closes — and unmounts — on click, so a call-site `onSuccess` would never fire.
 */
export const useLogout = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => authAPI.logout(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: authKeys.all });
    },
  });
};
